/**
 * Date utilities for migrating from moment.js to date-fns
 * This module provides timezone-aware date formatting with locale support
 */
import { format, formatInTimeZone, toZonedTime } from 'date-fns-tz';
import {
  parseISO,
  isValid,
  isSameDay,
  getHours,
  addDays as addDaysFns,
  Locale,
} from 'date-fns';
import { getTimezoneOffset as getTzOffset } from 'date-fns-tz';
import { enUS, ja, zhTW, es, ptBR, ru } from 'date-fns/locale';

// Locale mapping for date-fns
const localeMap: Record<string, Locale> = {
  en: enUS,
  ja: ja,
  tw: zhTW,
  es: es,
  pt: ptBR,
  ru: ru,
};

/**
 * Get date-fns locale object from locale code
 */
export function getDateFnsLocale(localeCode: string): Locale {
  return localeMap[localeCode] || enUS;
}

/**
 * Guess the user's timezone (replacement for moment.tz.guess())
 */
export function guessTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Parse a date value to a Date object
 * Handles ISO strings, timestamps, and Date objects
 */
export function parseDate(
  value: string | number | Date | null | undefined,
): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === 'number') {
    const date = new Date(value);
    return isValid(date) ? date : null;
  }
  if (typeof value === 'string') {
    // Try ISO parse first
    const parsed = parseISO(value);
    if (isValid(parsed)) return parsed;
    // Fall back to Date constructor
    const fallback = new Date(value);
    return isValid(fallback) ? fallback : null;
  }
  return null;
}

/**
 * Convert moment.js format tokens to date-fns format tokens
 */
function convertMomentFormat(momentFormat: string, locale: string): string {
  // Handle locale-specific AM/PM formats
  let result = momentFormat;

  // Moment uses single letters, date-fns uses multiple
  // Order matters - do longer tokens first
  result = result
    .replace(/YYYY/g, 'yyyy')
    .replace(/YY/g, 'yy')
    .replace(/DD/g, 'dd')
    .replace(/D/g, 'd')
    .replace(/MMMM/g, 'MMMM')
    .replace(/MMM/g, 'MMM')
    .replace(/MM/g, 'MM')
    .replace(/hh/g, 'hh')
    .replace(/HH/g, 'HH')
    .replace(/mm/g, 'mm')
    .replace(/ss/g, 'ss')
    .replace(/A/g, 'a') // AM/PM
    .replace(/\\d\\e/g, "'de'"); // Portuguese escaped literal

  // Handle M (month without leading zero) - only if not already converted
  // Need to be careful not to replace M in MMM or MMMM
  result = result.replace(/(?<![M])M(?![M])/g, 'M');

  // Handle h (12-hour without leading zero)
  result = result.replace(/(?<![h])h(?![h])/g, 'h');

  // Handle H (24-hour without leading zero)
  result = result.replace(/(?<![H])H(?![H])/g, 'H');

  return result;
}

/**
 * Format a date in a specific timezone with locale support
 * This is the main replacement for moment.tz(date, timezone).format(format)
 */
export function formatDateInTimezone(
  date: string | number | Date | null | undefined,
  timezone: string,
  formatStr: string,
  locale = 'en',
): string {
  const parsed = parseDate(date);
  if (!parsed) return '';

  const dateFnsLocale = getDateFnsLocale(locale);
  const convertedFormat = convertMomentFormat(formatStr, locale);

  return formatInTimeZone(parsed, timezone, convertedFormat, {
    locale: dateFnsLocale,
  });
}

/**
 * Format current time (replacement for moment().format())
 */
export function formatCurrentTime(formatStr: string, locale = 'en'): string {
  const dateFnsLocale = getDateFnsLocale(locale);
  const convertedFormat = convertMomentFormat(formatStr, locale);
  return format(new Date(), convertedFormat, { locale: dateFnsLocale });
}

/**
 * Get hour from a date in a specific timezone
 */
export function getHourInTimezone(
  date: string | number | Date | null | undefined,
  timezone: string,
): number {
  const parsed = parseDate(date);
  if (!parsed) return 0;
  const zonedDate = toZonedTime(parsed, timezone);
  return getHours(zonedDate);
}

/**
 * Check if two dates are the same day in a specific timezone
 */
export function isSameDayInTimezone(
  date1: string | number | Date | null | undefined,
  date2: string | number | Date | null | undefined,
  timezone: string,
): boolean {
  const parsed1 = parseDate(date1);
  const parsed2 = parseDate(date2);
  if (!parsed1 || !parsed2) return false;

  const zoned1 = toZonedTime(parsed1, timezone);
  const zoned2 = toZonedTime(parsed2, timezone);
  return isSameDay(zoned1, zoned2);
}

/**
 * Get date format string based on locale (replacement for MomentFormatter.getDateFormat)
 */
export function getDateFormat(locale: string): string {
  switch (locale) {
    case 'ja':
    case 'tw':
      return "M'月'd'日'";
    case 'es':
      return 'd MMM';
    case 'pt':
      return "d 'de' MMM";
    case 'ru':
      return 'd.M';
    default:
      return 'MMM d';
  }
}

/**
 * Get date format with year based on locale
 */
export function getDateFormatWithYear(locale: string): string {
  switch (locale) {
    case 'ja':
    case 'tw':
      return "yyyy'年'M'月'd'日'";
    case 'es':
      return 'd MMM yyyy';
    case 'pt':
      return "d 'de' MMM 'de' yyyy";
    case 'ru':
      return 'd.M.yyyy';
    default:
      return 'MMM d, yyyy';
  }
}

/**
 * Get time format based on locale and optional timestamp for Japanese locale edge case
 */
export function getTimeFormat(locale: string, hour?: number): string {
  if (locale === 'tw') {
    return 'aah:mm';
  }
  if (locale === 'ja') {
    if (hour !== undefined) {
      if (hour <= 11) {
        return 'aaH:mm';
      } else if (hour === 12) {
        return "aa'0':mm";
      } else {
        return 'aah:mm';
      }
    }
    return 'aah:mm';
  }
  return 'hh:mma';
}

/**
 * DateFormatter class - replacement for MomentFormatter
 */
export class DateFormatter {
  locale: string;
  timeZone: string;

  constructor(currentLocale = 'en', timeZone = 'Asia/Manila') {
    this.locale = currentLocale;
    this.timeZone = timeZone;
  }

  getDateFormat(): string {
    return getDateFormat(this.locale);
  }

  getDateFormatWithYear(): string {
    return getDateFormatWithYear(this.locale);
  }

  getTimeFormat(timestamp: string | number | Date | null = null): string {
    if (timestamp !== null) {
      const hour = getHourInTimezone(timestamp, this.timeZone);
      return getTimeFormat(this.locale, hour);
    }
    return getTimeFormat(this.locale);
  }

  getDateTimeFormat(timestamp: string | number | Date | null = null): string {
    const dateFormat = this.getDateFormatWithYear();
    const timeFormat = this.getTimeFormat(timestamp);
    const separator = this.locale === 'ja' || this.locale === 'tw' ? ' ' : ', ';

    if (timestamp === null) {
      return dateFormat + separator + timeFormat;
    }

    return formatDateInTimezone(
      timestamp,
      this.timeZone,
      dateFormat + separator + timeFormat,
      this.locale,
    );
  }

  lessonFormat(timestamp: string | number | Date): {
    formattedTime: string;
    formattedDate: string;
  } {
    const dateFormat = this.getDateFormatWithYear();
    const timeFormat = this.getTimeFormat(timestamp);
    const formattedTime = formatDateInTimezone(
      timestamp,
      this.timeZone,
      timeFormat,
      this.locale,
    );
    const formattedDate = formatDateInTimezone(
      timestamp,
      this.timeZone,
      dateFormat,
      this.locale,
    );
    return { formattedTime, formattedDate };
  }

  invoiceDateFormat(timestamp: string | number | Date): string {
    return formatDateInTimezone(
      timestamp,
      this.timeZone,
      this.getDateFormatWithYear(),
      this.locale,
    );
  }

  conversationItemFormat(timestamp: string | number | Date): string {
    const now = new Date();
    if (isSameDayInTimezone(timestamp, now, this.timeZone)) {
      return formatDateInTimezone(
        timestamp,
        this.timeZone,
        this.getTimeFormat(timestamp),
        this.locale,
      );
    }
    return formatDateInTimezone(
      timestamp,
      this.timeZone,
      this.getDateFormat(),
      this.locale,
    );
  }

  messageItemFormat(timestamp: string | number | Date): string {
    return formatDateInTimezone(
      timestamp,
      this.timeZone,
      this.getTimeFormat(timestamp),
      this.locale,
    );
  }
}

/**
 * Add days to a date (replacement for moment().add(n, 'days'))
 */
export function addDays(
  date: Date | string | number | null | undefined,
  days: number,
): Date {
  const parsed = parseDate(date) || new Date();
  return addDaysFns(parsed, days);
}

/**
 * Get timezone offset in minutes (replacement for moment.tz(date, tz).utcOffset())
 */
export function getTimezoneOffsetMinutes(
  timezone: string,
  date: Date = new Date(),
): number {
  return getTzOffset(timezone, date) / (60 * 1000);
}

/**
 * Format offset as GMT string (e.g., "GMT+9" or "GMT-5")
 */
export function formatTimezoneOffset(
  timezone: string,
  date: Date = new Date(),
): string {
  const offsetMinutes = getTimezoneOffsetMinutes(timezone, date);
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours >= 0 ? '+' : '';
  const offsetStr = Number.isInteger(offsetHours)
    ? offsetHours.toString()
    : offsetHours.toFixed(1);
  return `GMT${sign}${offsetStr}`;
}

/**
 * Convert moment.js day-of-week format tokens to date-fns format tokens
 * This handles dddd (full day name) and ddd (abbreviated day name)
 */
function convertDayFormat(formatStr: string): string {
  return formatStr
    .replace(/dddd/g, 'EEEE') // Full day name
    .replace(/ddd/g, 'EEE') // Abbreviated day name
    .replace(/dd/g, 'EEEEEE'); // Two-letter day name
}

/**
 * Convert moment.js format string to date-fns format string
 * Handles day names in addition to the basic format tokens
 */
export function convertMomentFormatFull(momentFormat: string): string {
  let result = momentFormat;

  // Convert day-of-week tokens first
  result = convertDayFormat(result);

  // Convert date/time tokens
  result = result
    .replace(/YYYY/g, 'yyyy')
    .replace(/YY/g, 'yy')
    .replace(/DD/g, 'dd')
    .replace(/MMMM/g, 'MMMM')
    .replace(/MMM/g, 'MMM')
    .replace(/MM/g, 'MM')
    .replace(/HH/g, 'HH')
    .replace(/hh/g, 'hh')
    .replace(/mm/g, 'mm')
    .replace(/ss/g, 'ss')
    .replace(/A/g, 'a')
    .replace(/\\d\\e/g, "'de'");

  // Handle single D (day without leading zero)
  result = result.replace(/(?<![d])D(?![d])/g, 'd');

  // Handle single M (month without leading zero)
  result = result.replace(/(?<![M])M(?![M月])/g, 'M');

  // Handle single h (12-hour without leading zero)
  result = result.replace(/(?<![h])h(?![h])/g, 'h');

  // Handle single H (24-hour without leading zero)
  result = result.replace(/(?<![H])H(?![H])/g, 'H');

  return result;
}

/**
 * Format a date in a timezone with full moment.js format support including day names
 */
export function formatInTz(
  date: Date | string | number | null | undefined,
  timezone: string,
  formatStr: string,
  locale = 'en',
): string {
  const parsed = parseDate(date);
  if (!parsed) return '';

  const dateFnsLocale = getDateFnsLocale(locale);
  const convertedFormat = convertMomentFormatFull(formatStr);

  return formatInTimeZone(parsed, timezone, convertedFormat, {
    locale: dateFnsLocale,
  });
}

/**
 * Format current date with full moment.js format support
 */
export function formatNow(formatStr: string, locale = 'en'): string {
  const dateFnsLocale = getDateFnsLocale(locale);
  const convertedFormat = convertMomentFormatFull(formatStr);
  return format(new Date(), convertedFormat, { locale: dateFnsLocale });
}

/**
 * Get a date with days added, formatted in a timezone
 */
export function formatAddedDays(
  baseDate: Date | null | undefined,
  daysToAdd: number,
  timezone: string,
  formatStr: string,
  locale = 'en',
): string {
  const base = baseDate || new Date();
  const targetDate = addDaysFns(base, daysToAdd);
  return formatInTz(targetDate, timezone, formatStr, locale);
}

// Export default for backwards compatibility
export default DateFormatter;
